import axios from 'axios';
import type { MinecraftConsoleResponse, OwnerActionLogSummary } from '../types/server';
import type { AuthResponse, User } from '../types/auth';
import type { GameFieldConfig } from '../types/package';
import type {
  AdminHostAgentDetail,
  AdminOperationDiagnostic,
  ReconciliationReport,
} from '../types/admin';
import type { ServerBackup } from '../types/server';
import type {
  CustomInstanceDetail,
  CustomBoardCard,
  CustomBoardCardInput,
  CustomInstanceListPagination,
  CustomInstanceListParams,
  CustomInstanceMutationSnapshot,
  CustomInstanceSummary,
  CustomIntegrationKey,
  CustomLogSnapshot,
  CustomProduct,
  CustomProductDefinition,
  CustomProductImage,
  CustomProductRevision,
  CustomUploadStatus,
} from '../types/custom';

// In production, use relative path (served by same Express server)
// In development, vite.config.ts proxy handles /api -> backend
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && localStorage.getItem('token')) {
      localStorage.removeItem('token');
      if (window.location.pathname !== '/login') window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: async (email: string, password: string): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/login', { email, password });
    return response.data;
  },

  register: async (email: string, password: string, name: string): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/register', { email, password, name });
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get<User>('/auth/me');
    return response.data;
  },

  logout: async (token?: string): Promise<void> => {
    await api.post('/auth/logout', undefined, token ? {
      headers: { Authorization: `Bearer ${token}` },
    } : undefined);
  },
};

export const serverApi = {
  getServers: async () => {
    const response = await api.get('/servers');
    return response.data;
  },

  getServer: async (id: string) => {
    const response = await api.get(`/servers/${id}`);
    return response.data;
  },

  getUserServers: async () => {
    const response = await api.get('/servers');
    return response.data;
  },

  createServer: async (data: any) => {
    const response = await api.post('/servers', data);
    return response.data;
  },

  updateServer: async (id: string, data: any) => {
    const response = await api.post(`/servers/${id}/${data.action}`);
    return response.data;
  },

  deleteServer: async (id: string) => {
    const response = await api.delete(`/servers/${id}`);
    return response.data;
  },

  startServer: async (id: string) => {
    const response = await api.post(`/servers/${id}/start`);
    return response.data;
  },

  stopServer: async (id: string) => {
    const response = await api.post(`/servers/${id}/stop`);
    return response.data;
  },

  restartServer: async (id: string) => {
    const response = await api.post(`/servers/${id}/restart`);
    return response.data;
  },

  executeMinecraftConsoleCommand: async (
    id: string,
    request: { commandId: string; command: string },
  ): Promise<MinecraftConsoleResponse> => {
    const response = await api.post(`/servers/${id}/console/commands`, request);
    return response.data as MinecraftConsoleResponse;
  },

  getServerFiles: async (id: string, path?: string) => {
    const response = await api.get(`/servers/${id}/files`, {
      params: { path: path || '/' }
    });
    return response.data;
  },

  uploadFile: async (id: string, file: File, path: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', path);
    
    const response = await api.post(`/servers/${id}/files/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  downloadFile: async (id: string, filePath: string) => {
    const response = await api.get(`/servers/${id}/files/download`, {
      params: { path: filePath },
      responseType: 'blob',
    });
    return response.data;
  },

  deleteFile: async (id: string, filePath: string) => {
    const response = await api.delete(`/servers/${id}/files`, {
      params: { path: filePath }
    });
    return response.data;
  },

  createFolder: async (id: string, folderPath: string) => {
    const response = await api.post(`/servers/${id}/files/folder`, {
      path: folderPath
    });
    return response.data;
  },

  getActionLogs: async (id: string, limit = 20) => {
    const response = await api.get<{ success: true; logs: OwnerActionLogSummary[] }>(`/servers/${id}/action-logs`, {
      params: { limit },
    });
    return response.data;
  },

  getBackups: async (id: string) => {
    const response = await api.get<{ success: true; retentionCount: number; backups: ServerBackup[] }>(
      `/servers/${id}/backups`,
    );
    return response.data;
  },

  createBackup: async (id: string) => {
    const response = await api.post<{ success: true; backup: ServerBackup }>(`/servers/${id}/backups`);
    return response.data;
  },

  restoreBackup: async (serverId: string, backupId: string) => {
    const response = await api.post<{
      success: true;
      result: {
        backupId: string;
        serverId: string;
        operationId: string;
        actionId: string;
        status: 'accepted';
      };
    }>(`/servers/${serverId}/backups/${backupId}/restore`);
    return response.data;
  },

  getBackupDownload: async (serverId: string, backupId: string) => {
    const response = await api.get<{
      success: true;
      download: { url: string; expiresAt: string; filename: string };
    }>(`/servers/${serverId}/backups/${backupId}/download`);
    return response.data;
  },
};

export const packageApi = {
  getPackages: async () => {
    const response = await api.get('/packages');
    return response.data;
  },
  getGameFieldConfigs: async (gameType: string) => {
    const response = await api.get(`/packages/game/${gameType}/fields`);
    return response.data as { success: boolean; game: string; fieldConfigs: GameFieldConfig[] };
  },
  getPackageFieldConfigs: async (packageId: string) => {
    const response = await api.get(`/packages/${packageId}/fields`);
    return response.data as { success: boolean; game: string; packageId: string; fieldConfigs: GameFieldConfig[] };
  },
};

export const adminApi = {
  getHostAgents: async (params?: Record<string, any>) => {
    const response = await api.get('/admin/host-agents', { params });
    return response.data;
  },

  getHostAgent: async (id: string): Promise<AdminHostAgentDetail> => {
    const response = await api.get(`/admin/host-agents/${id}`);
    return response.data;
  },

  createHostAgent: async (data: any) => {
    const response = await api.post('/admin/host-agents', data);
    return response.data;
  },

  updateHostAgent: async (id: string, data: any) => {
    // Backend exposes PATCH /admin/host-agents/:id
    const response = await api.patch(`/admin/host-agents/${id}`, data);
    return response.data;
  },

  deleteHostAgent: async (id: string) => {
    const response = await api.delete(`/admin/host-agents/${id}`);
    return response.data;
  },

  checkHostAgentHealth: async (id: string) => {
    const response = await api.post(`/admin/host-agents/${id}/check-health`);
    return response.data;
  },

  initiateHostAgentConnection: async (id: string) => {
    const response = await api.post(`/admin/host-agents/${id}/initiate-connection`);
    return response.data;
  },

  disconnectHostAgent: async (id: string) => {
    const response = await api.post(`/admin/host-agents/${id}/disconnect`);
    return response.data;
  },

  initializeSSH: async (id: string) => {
    const response = await api.post(`/admin/host-agents/${id}/ssh`);
    return response.data;
  },

  rotateHostAgentToken: async (id: string, agentToken?: string) => {
    const response = await api.post(`/admin/host-agents/${id}/rotate-token`, agentToken ? { agentToken } : {});
    return response.data as { success: boolean; message: string; agentId: string; agentToken: string };
  },

  getHostAgentReconciliation: async (id: string): Promise<{ success: true; report: ReconciliationReport }> => {
    const response = await api.get(`/admin/host-agents/${id}/reconciliation`);
    return response.data;
  },

  adoptHostAgentOrphan: async (id: string, target: { containerName: string; dockerId: string }) => {
    const response = await api.post(`/admin/host-agents/${id}/orphans/adopt`, target);
    return response.data;
  },

  deleteHostAgentOrphan: async (id: string, target: { containerName: string; dockerId: string }) => {
    const response = await api.post(`/admin/host-agents/${id}/orphans/delete`, target);
    return response.data;
  },

  diagnoseOperation: async (identifier: string): Promise<AdminOperationDiagnostic> => {
    const response = await api.get(`/admin/diagnostics/operations/${encodeURIComponent(identifier)}`);
    return response.data;
  },

  reconcileOperation: async (identifier: string, expectedOperationId: string) => {
    const response = await api.post(
      `/admin/diagnostics/operations/${encodeURIComponent(identifier)}/reconcile`,
      { expectedOperationId },
    );
    return response.data as { success: true; message: string };
  },

  generateMissingHostAgentTokens: async () => {
    const response = await api.post(`/admin/host-agents/generate-missing-tokens`);
    return response.data as { success: boolean; updated: number };
  },

  getPackages: async () => {
    const response = await api.get('/admin/packages');
    return response.data;
  },

  createPackage: async (data: any) => {
    const response = await api.post('/admin/packages', data);
    return response.data;
  },

  updatePackage: async (id: string, data: any) => {
    const response = await api.put(`/admin/packages/${id}`, data);
    return response.data;
  },

  deletePackage: async (id: string) => {
    const response = await api.delete(`/admin/packages/${id}`);
    return response.data;
  },

  getSystemStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  getDashboardStats: async () => {
    const response = await api.get('/admin/dashboard-stats');
    return response.data;
  },
};

export const customAdminApi = {
  listBoardCards: async (productId: string) => {
    const response = await api.get(`/admin/custom-products/${productId}/board/cards`);
    return response.data as { success: true; cards: CustomBoardCard[] };
  },

  createBoardCard: async (productId: string, data: CustomBoardCardInput) => {
    const response = await api.post(`/admin/custom-products/${productId}/board/cards`, data);
    return response.data as { success: true; card: CustomBoardCard };
  },

  updateBoardCard: async (productId: string, cardId: string, data: Partial<CustomBoardCardInput> & { version: number }) => {
    const response = await api.patch(`/admin/custom-products/${productId}/board/cards/${cardId}`, data);
    return response.data as { success: true; card: CustomBoardCard };
  },

  deleteBoardCard: async (productId: string, cardId: string, version: number) => {
    const response = await api.delete(`/admin/custom-products/${productId}/board/cards/${cardId}`, { data: { version } });
    return response.data as { success: true };
  },

  listIntegrationKeys: async (productId: string) => {
    const response = await api.get(`/admin/custom-products/${productId}/integration/keys`);
    return response.data as { success: true; keys: CustomIntegrationKey[] };
  },

  createIntegrationKey: async (productId: string, name: string) => {
    const response = await api.post(`/admin/custom-products/${productId}/integration/keys`, { name });
    return response.data as { success: true; key: CustomIntegrationKey; apiKey: string };
  },

  revokeIntegrationKey: async (productId: string, keyId: string) => {
    const response = await api.delete(`/admin/custom-products/${productId}/integration/keys/${keyId}`);
    return response.data as { success: true; key: CustomIntegrationKey };
  },

  listProducts: async (params?: { status?: CustomProduct['status']; key?: string }) => {
    const response = await api.get('/admin/custom-products', { params });
    return response.data as { success: true; products: CustomProduct[] };
  },

  createProduct: async (data: { key: string; displayName: string; description?: string }) => {
    const response = await api.post('/admin/custom-products', data);
    return response.data as { success: true; product: CustomProduct };
  },

  getProduct: async (productId: string) => {
    const response = await api.get(`/admin/custom-products/${productId}`);
    return response.data as {
      success: true;
      product: CustomProduct;
      revisions: CustomProductRevision[];
    };
  },

  updateProduct: async (productId: string, data: { displayName?: string; description?: string }) => {
    const response = await api.patch(`/admin/custom-products/${productId}`, data);
    return response.data as { success: true; product: CustomProduct };
  },

  deleteProduct: async (productId: string) => {
    const response = await api.delete(`/admin/custom-products/${productId}`);
    return response.data as { success: true };
  },

  createRevision: async (productId: string, definition: CustomProductDefinition) => {
    const response = await api.post(`/admin/custom-products/${productId}/revisions`, { definition });
    return response.data as { success: true; revision: CustomProductRevision };
  },

  activateProduct: async (productId: string) => {
    const response = await api.post(`/admin/custom-products/${productId}/activate`, {});
    return response.data as { success: true; product: CustomProduct };
  },

  disableProduct: async (productId: string) => {
    const response = await api.post(`/admin/custom-products/${productId}/disable`, {});
    return response.data as { success: true; product: CustomProduct };
  },

  listImages: async (productId: string) => {
    const response = await api.get(`/admin/custom-products/${productId}/images`);
    return response.data as { success: true; productId: string; images: CustomProductImage[] };
  },

  copyImage: async (productId: string, sourceImageId: string, data: {
    version: string;
    productRevision: number;
    idempotencyKey: string;
  }) => {
    const response = await api.post(`/admin/custom-products/${productId}/images/${sourceImageId}/copy`, data);
    return response.data as { success: true; image: CustomProductImage };
  },

  initiateImageUpload: async (productId: string, data: {
    version: string;
    productRevision: number;
    sha256: string;
    sizeBytes: number;
    idempotencyKey: string;
  }) => {
    const response = await api.post(`/admin/custom-products/${productId}/images/uploads`, data);
    return response.data as { success: true; image: CustomProductImage; upload: CustomUploadStatus['upload'] };
  },

  getImageUploadStatus: async (productId: string, imageId: string) => {
    const response = await api.get(`/admin/custom-products/${productId}/images/${imageId}/upload`);
    return response.data as { success: true } & CustomUploadStatus;
  },

  presignImageParts: async (productId: string, imageId: string, partNumbers: number[]) => {
    const response = await api.post(
      `/admin/custom-products/${productId}/images/${imageId}/upload-parts`,
      { partNumbers },
    );
    return response.data as {
      success: true;
      image: CustomProductImage;
      urls: Array<{ partNumber: number; sizeBytes: number; expiresAt: string; url: string }>;
    };
  },

  completeImageUpload: async (productId: string, imageId: string, idempotencyKey: string) => {
    const response = await api.post(
      `/admin/custom-products/${productId}/images/${imageId}/complete-upload`,
      { idempotencyKey },
    );
    return response.data as { success: true; image: CustomProductImage };
  },

  requestImageBuild: async (productId: string, imageId: string, agentId: string, idempotencyKey: string) => {
    const response = await api.post(
      `/admin/custom-products/${productId}/images/${imageId}/builds`,
      { agentId },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    );
    return response.data as { success: true; operationId: string; image: CustomProductImage };
  },

  retireImage: async (productId: string, imageId: string) => {
    const response = await api.post(`/admin/custom-products/${productId}/images/${imageId}/retire`, {});
    return response.data as { success: true; image: CustomProductImage };
  },

  deleteImage: async (productId: string, imageId: string, idempotencyKey: string) => {
    const response = await api.delete(`/admin/custom-products/${productId}/images/${imageId}`, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
    return response.data as { success: true; image: CustomProductImage };
  },

  listInstances: async (params: CustomInstanceListParams = {}) => {
    const response = await api.get('/admin/custom-instances', { params });
    return response.data as {
      success: true;
      instances: CustomInstanceSummary[];
      pagination: CustomInstanceListPagination;
    };
  },

  getInstance: async (instanceId: string) => {
    const response = await api.get(`/admin/custom-instances/${instanceId}`);
    return response.data as { success: true; instance: CustomInstanceDetail };
  },

  createInstance: async (data: {
    productId: string;
    imageId: string;
    imageReplicaId: string;
    agentId: string;
    name: string;
    parameters: Record<string, unknown>;
    resourceProfile?: string;
  }, idempotencyKey: string) => {
    const response = await api.post('/admin/custom-instances', data, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
    return response.data as { success: true; operationId: string; instance: CustomInstanceMutationSnapshot };
  },

  lifecycleInstance: async (
    instanceId: string,
    action: 'start' | 'stop' | 'restart' | 'delete',
    idempotencyKey: string,
  ) => {
    const response = action === 'delete'
      ? await api.delete(`/admin/custom-instances/${instanceId}`, {
        headers: { 'Idempotency-Key': idempotencyKey },
      })
      : await api.post(`/admin/custom-instances/${instanceId}/${action}`, {}, {
        headers: { 'Idempotency-Key': idempotencyKey },
      });
    return response.data as { success: true; operationId: string; instance: CustomInstanceMutationSnapshot };
  },

  getInstanceLogs: async (instanceId: string, tail = 200) => {
    const response = await api.get(`/admin/custom-instances/${instanceId}/logs`, { params: { tail } });
    return response.data as CustomLogSnapshot;
  },
};

export const commandTemplatesApi = {
  // Get all templates (filtered by user role automatically)
  getTemplates: async (params?: { category?: string; targetType?: string; search?: string }) => {
    const response = await api.get('/command-templates', { params });
    return response.data;
  },

  // Get single template with full details
  getTemplate: async (id: string) => {
    const response = await api.get(`/command-templates/${id}`);
    return response.data;
  },

  // Execute a template on a target
  executeTemplate: async (id: string, data: { 
    agentId?: string; 
    containerId?: string; 
    serverId?: string;
    params?: Record<string, any>;
  }) => {
    const response = await api.post(`/command-templates/${id}/execute`, data);
    return response.data;
  },

  // Create new template (admin only)
  createTemplate: async (data: any) => {
    const response = await api.post('/command-templates', data);
    return response.data;
  },

  // Update template (admin only)
  updateTemplate: async (id: string, data: any) => {
    const response = await api.put(`/command-templates/${id}`, data);
    return response.data;
  },

  // Delete template (admin only)
  deleteTemplate: async (id: string) => {
    const response = await api.delete(`/command-templates/${id}`);
    return response.data;
  },

  // Get usage statistics (admin only)
  getStats: async () => {
    const response = await api.get('/command-templates/admin/stats');
    return response.data;
  },

  // Execute an ad-hoc command on an agent with SSE streaming
  executeStream: (agentId: string, command: string, callbacks: {
    onStatus?: (data: { phase: string; message: string }) => void;
    onCommand?: (data: { command: string }) => void;
    onStdout?: (data: { line: string }) => void;
    onStderr?: (data: { line: string }) => void;
    onDone?: (data: { code: number | null; signal: string | null }) => void;
    onError?: (data: { message: string }) => void;
  }): AbortController => {
    const controller = new AbortController();
    const token = localStorage.getItem('token') || '';

    fetch(`${api.defaults.baseURL}/command-templates/execute-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ agentId, command }),
      signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok || !response.body) {
        callbacks.onError?.({ message: `HTTP ${response.status}` });
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7);
          } else if (line.startsWith('data: ') && currentEvent) {
            try {
              const data = JSON.parse(line.slice(6));
              switch (currentEvent) {
                case 'status': callbacks.onStatus?.(data); break;
                case 'command': callbacks.onCommand?.(data); break;
                case 'stdout': callbacks.onStdout?.(data); break;
                case 'stderr': callbacks.onStderr?.(data); break;
                case 'done': callbacks.onDone?.(data); break;
                case 'error': callbacks.onError?.(data); break;
              }
            } catch {}
            currentEvent = '';
          }
        }
      }
    }).catch((err) => {
      if (err.name !== 'AbortError') {
        callbacks.onError?.({ message: err.message });
      }
    });

    return controller;
  },
};

export const votesApi = {
  getVotes: async () => {
    const response = await api.get('/votes');
    return response.data as { success: boolean; votes: Record<string, number> };
  },

  getMyVotes: async () => {
    const response = await api.get('/votes/me');
    return response.data as { success: boolean; votedGames: string[] };
  },

  vote: async (game: string) => {
    const response = await api.post(`/votes/${encodeURIComponent(game)}`);
    return response.data;
  },
};

export default api;
